import React from 'react';

import uniqBy from 'lodash/uniqBy';

import { ReadOnlyPipelineCommentsProvider, useCommentContext } from '../../contexts/CommentData/CommentData';
import useModelSearch, { ModelSearchProvider } from '../../models/ModelSearchContext';
import usePipelineSearch, { PipelineSearchProvider } from '../../pipelines/PipelineSearchContext';
import useClientContext from '../../contexts/ClientData/ClientData';
import useUserContext from '../../contexts/UserData/UserData';
import useWorkspaceContext from '../../workspaces/WorkspaceContext/WorkspaceContext';

import { getCommentsWithPipelines } from '../../../utils/product_utils';
import { getPipelineActivityData } from '../../ActivityLog/activity-utils';
import { getThemeColor } from '../../../utils/theme-utils';
import { getUserDisplayName } from '../../../utils/name_utils';

import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import ErrorLabel from '../../common/Labels/ErrorLabel';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import LinkLabel from '../../common/Labels/LinkLabel';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import PageLayout from '../PageLayout';
import PrimaryContent from '../layouts/PrimaryContent';
import SecondaryContent from '../layouts/SecondaryContent';

import ModelsIcon from '../../common/icons/ModelsIcon';
import PipelineIcon from '../../common/icons/PipelineIcon';
import WorkspaceIcon from '../../common/icons/WorkspaceIcon';

import ActivityLog from '../../ActivityLog/ActivityLog';
import JupyterView from '../../JupyterView/JupyterView';
import WorkspaceCollaboratorsView from '../../workspaces/WorkspaceCollaboratorsView/WorkspaceCollaboratorsView';
import { preserveWorkspaceURL } from '../../../utils/url_utils';
import usePageTitle from '../../../utils/hooks/use_page_title';

const ActiveWorkspaceView = () => {
  const {
    currentWorkspace,
    currentWorkspaceError,
    defaultWorkspace,
    isSelectedWorkspaceLoading,
    error,
    loading
  } = useWorkspaceContext();
  const { userData } = useUserContext()
  const owner = currentWorkspace?.owner;

  const errorMsg = error || currentWorkspaceError;
  const currentWorkspaceName = currentWorkspace
    ? currentWorkspace.name.endsWith(' - Default Workspace') && currentWorkspace.createdBy === userData?.id
      ? 'My Default Workspace'
      : currentWorkspace.name
    : null
  const displayText = (loading || isSelectedWorkspaceLoading) ? 'Loading Workspace...' : errorMsg ? 'Error Loading Workspace' : currentWorkspaceName || 'Unknown Workspace';
  const displayPrivacy = currentWorkspace?.visibility || null;

  const defaultWorkspaceNotFound = !defaultWorkspace;
  const secondaryDisplayText = (loading || isSelectedWorkspaceLoading) ? null : owner ? getUserDisplayName(owner) : errorMsg?.toString() || 'Removed User';

  return (
    <div className="relative">
      {
        (loading || isSelectedWorkspaceLoading) ? (
          <LoadingSpinner animationKey="model-list-spinner" size="medium" useOverlay />
        ) : null
      }
      <CardView className="p-4" disableFullHeight>
        <div className="flex flex-col justify-start items-start w-full">
          <div className="mb-2 flex items-center">
            <Icon fill={getThemeColor('brand')} icon={WorkspaceIcon} size={24} useTailwindSizing />
            <CardHeader className="ml-2" size="xl" bold>{displayText}</CardHeader>
          </div>
          <div className="ml-10 flex items-center">
            {
              defaultWorkspaceNotFound ? (
                <ErrorLabel bold>Default Workspace not found. Please click on &quot;View Jupyter Hub&quot; on the right to get started with your workspace.</ErrorLabel>
              ) : (
                <Label>{secondaryDisplayText}</Label>
              )
            }
            {
              displayPrivacy && (
                <>
                  <span className="mx-1">&middot;</span>
                  <Label>{displayPrivacy}</Label>
                </>
              )
            }
          </div>
        </div>
      </CardView>
    </div>
  )
};

const ActivePipelineSummaryView = () => {
  const { pipelines } = usePipelineSearch()
  const activePipelines = pipelines?.filter(({ status }) => status === 'Active') || [];
  const uniquePipelines = uniqBy(pipelines, 'pipelineID');
  const uniqueActivePipelines = uniqBy(activePipelines, 'pipelineID');

  const numberOfPipelines = uniquePipelines?.length || 0;
  const numberOfActivePipelines = uniqueActivePipelines?.length || 0;
  return (
    <CardView className="mt-2 p-4" disableFullHeight>
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center">
          <Icon fill={getThemeColor('brand')} icon={PipelineIcon} size={24} useTailwindSizing />
          <Label className="ml-2" bold>
            {!numberOfPipelines ? 'No Pipelines' : `${numberOfActivePipelines}/${numberOfPipelines} Active Pipeline${numberOfPipelines === 1 && numberOfActivePipelines === 1 ? '' : 's'}`}
          </Label>
        </div>
        <LinkLabel to={preserveWorkspaceURL('/pipelines')}>View Pipelines</LinkLabel>
      </div>
    </CardView>
  )
}

const ModelsSummaryView = () => {
  const { models } = useModelSearch();
  const numberOfModels = uniqBy(models, 'modelID').length || 0;

  return (
    <CardView className="mt-2 p-4" disableFullHeight>
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center">
          <Icon fill={getThemeColor('brand')} icon={ModelsIcon} size={24} useTailwindSizing />
          <Label className="ml-2" bold>
            {!numberOfModels ? 'No' : `${numberOfModels}`} Model{numberOfModels === 1 ? '' : 's'}
          </Label>
        </div>
        <LinkLabel to={preserveWorkspaceURL('/models')}>View Models</LinkLabel>
      </div>
    </CardView>
  )
}

const ActivityLogWrapper = () => {
  const { isAuthEnabled } = useClientContext();
  const { userData } = useUserContext();
  const { comments: commentsWithUsers, error, loading } = useCommentContext();
  const { pipelines } = usePipelineSearch();

  if (!isAuthEnabled) {
    return null;
  }

  const activityData = getPipelineActivityData({
    comments: getCommentsWithPipelines(pipelines, commentsWithUsers),
    currentUser: userData,
  })
  return (
    <div className="my-2 flex-grow-2 w-full min-w-min">
      <ActivityLog
        id="home-activity-log"
        height={480}
        activityData={activityData}
        commentError={error}
        areCommentsLoading={loading}
        shouldShowPipelineName
        disableScrollFading
      />
    </div>
  )
}

export const Home = () => {
  usePageTitle('Home | Wallaroo');

  return <PageLayout>
    <PipelineSearchProvider>
      <PrimaryContent>
        <ActiveWorkspaceView />
        <ActivePipelineSummaryView />
        <ModelSearchProvider>
          <ModelsSummaryView />
        </ModelSearchProvider>
      </PrimaryContent>
      <SecondaryContent>
        <JupyterView />
        <WorkspaceCollaboratorsView />
        <ReadOnlyPipelineCommentsProvider>
          <ActivityLogWrapper />
        </ReadOnlyPipelineCommentsProvider>
      </SecondaryContent>
    </PipelineSearchProvider>
  </PageLayout>
};

export default Home;
